import { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const stripeKey = process.env.REACT_APP_STRIPE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const PLAN_DISPLAY = {
  launch_monthly: { name: 'GoldenHour Launch', price: '$19', interval: 'month', renewal: 'Billed monthly. Auto-renews until you cancel.' },
  launch_yearly: { name: 'GoldenHour Launch', price: '$192', interval: 'year', renewal: 'Billed yearly ($16/mo). Auto-renews annually until you cancel.' },
  pro_monthly: { name: 'GoldenHour Pro', price: '$39', interval: 'month', renewal: 'Billed monthly. Auto-renews until you cancel.' },
  pro_yearly: { name: 'GoldenHour Pro', price: '$396', interval: 'year', renewal: 'Billed yearly ($33/mo). Auto-renews annually until you cancel.' },
};

const EmbeddedCheckoutModal = ({ planKey, returnPath = '/dashboard', onClose, onSuccess }) => {
  const [error, setError] = useState('');
  const plan = PLAN_DISPLAY[planKey] || { name: 'GoldenHour', price: '', interval: '', renewal: '' };

  const fetchClientSecret = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_key: planKey,
        origin_url: window.location.origin,
        return_path: returnPath,
      }, { withCredentials: true });
      return res.data.client_secret;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not start checkout.';
      setError(msg);
      throw err;
    }
  }, [planKey, returnPath]);

  const handleComplete = useCallback(() => {
    if (onSuccess) onSuccess();
  }, [onSuccess]);

  if (!stripePromise) {
    return <p style={{ color: '#c47272', textAlign: 'center', padding: '20px', fontFamily: 'var(--sans)' }}>Payment not configured.</p>;
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: '16px', maxWidth: '520px', width: '95%', maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,.15)',
      }}>
        {/* FIX #8: Branded header with plan details + FIX #1/11: Recurring billing disclosure */}
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #e8e3dc', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p style={{ fontFamily: 'var(--serif)', fontSize: '18px', color: '#1F1D1B', marginBottom: '4px', letterSpacing: '-.02em' }}>
              golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em>
            </p>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '16px', fontWeight: 600, color: '#1F1D1B', marginBottom: '2px' }}>
              {plan.name} — {plan.price}/{plan.interval}
            </p>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782', lineHeight: 1.5 }}>
              {plan.renewal}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8c8782', cursor: 'pointer', padding: '4px' }} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {error ? (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <p style={{ color: '#c47272', marginBottom: '16px', fontFamily: 'var(--sans)', fontSize: '14px' }}>{error}</p>
            <button onClick={onClose} style={{ padding: '10px 24px', background: '#1F1D1B', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: '13px', borderRadius: '8px' }}>
              Go Back
            </button>
          </div>
        ) : (
          <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret, onComplete: handleComplete }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}

        {/* Trust footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #e8e3dc', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <Shield size={12} style={{ color: '#8c8782' }} />
          <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>
            Secure checkout powered by Stripe. Cancel anytime. No refunds — AI generation costs apply.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedCheckoutModal;
